import { useEffect, useRef } from "react";
import { changeActiveImage } from "@default/lib/change-active-image";

/**
 * Attaches a single IntersectionObserver to all `.scroll-overview-listener`
 * elements inside containerRef. When an item enters the central 30% of the
 * viewport it becomes active: changeActiveImage updates the CSS translateY var
 * on the sticky image stack, and onActiveChange is called with the item's id
 * so the parent can drive aria-current via React state.
 *
 * When multiple items enter the zone in the same frame (fast scroll), the one
 * whose center is closest to the viewport center wins — preventing elements
 * from being skipped.
 *
 * Re-attaches whenever mappedItems changes (e.g. endless-scroll loads more).
 */
const useScrollActive = (mappedItems, containerRef, onActiveChange) => {
  const observerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !mappedItems?.length) return;

    observerRef.current?.disconnect();

    const listItems = containerRef.current.querySelectorAll(".scroll-overview-listener");

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const intersecting = entries.filter((e) => e.isIntersecting);
        if (!intersecting.length) return;

        // Pick the entry whose center is closest to the viewport center.
        // This prevents skipping when fast scrolling batches multiple entries.
        const viewportCenter = window.innerHeight / 2;
        const best = intersecting.reduce((prev, entry) => {
          const { top, height } = entry.boundingClientRect;
          const distance = Math.abs(top + height / 2 - viewportCenter);
          return distance < prev.distance ? { entry, distance } : prev;
        }, { entry: intersecting[0], distance: Infinity });

        changeActiveImage(best.entry.target, containerRef.current);
        onActiveChange(best.entry.target.id);
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );

    listItems.forEach((el) => observerRef.current.observe(el));

    return () => observerRef.current?.disconnect();
  }, [mappedItems]);
};

export default useScrollActive;
